export type TypeDeepMergeOptions = {
    replaceArrays?: boolean;
};

type TypeDeepMergeTarget = Record<string, any>;

// ----------------------------------------------------------------------------

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const hasOwn = Object.hasOwn;

function isUnsafeKey(key: string): boolean {
    return UNSAFE_KEYS.has(key);
}

function isObjectLike(value: any): boolean {
    return value != null && typeof value === 'object';
}

function isMergeableObject(value: any): boolean {
    if (!isObjectLike(value) || Array.isArray(value)) {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}

function mergeInto(target: TypeDeepMergeTarget, source: any, deep: boolean, replaceArrays: boolean): void {
    if (!isMergeableObject(source) && !Array.isArray(source)) {
        return;
    }

    if (!deep) {
        for (const key in source) {
            if (!hasOwn(source, key) || isUnsafeKey(key)) {
                continue;
            }

            const srcVal = source[key];

            target[key] = Array.isArray(srcVal) ? srcVal.slice() : srcVal;
        }

        return;
    }

    for (const key in source) {
        if (!hasOwn(source, key) || isUnsafeKey(key)) {
            continue;
        }

        const srcVal = source[key];
        const tgtVal = target[key];
        const srcIsObject = isMergeableObject(srcVal);

        if (srcIsObject) {
            target[key] = isMergeableObject(tgtVal)
                ? mergeObjectValue(tgtVal, srcVal, replaceArrays)
                : mergeObjectValue({}, srcVal, replaceArrays);

            continue;
        }

        if (Array.isArray(srcVal)) {
            target[key] = mergeArrayValue(srcVal, tgtVal, replaceArrays);
            continue;
        }

        target[key] = srcVal;
    }
}

function mergeObjectValue(
    target: TypeDeepMergeTarget,
    source: TypeDeepMergeTarget,
    replaceArrays: boolean,
): TypeDeepMergeTarget {
    mergeInto(target, source, true, replaceArrays);

    return target;
}

function mergeArrayValue(source: any[], target: any, replaceArrays: boolean): any[] {
    const nextTarget = replaceArrays || !Array.isArray(target) ? [] : target;

    nextTarget.length = source.length;

    for (let i = 0; i < source.length; i++) {
        nextTarget[i] = mergeArrayItem(source[i], nextTarget[i], replaceArrays);
    }

    return nextTarget;
}

function mergeArrayItem(srcVal: any, tgtVal: any, replaceArrays: boolean): any {
    if (!isMergeableObject(srcVal)) {
        return srcVal;
    }

    if (isMergeableObject(tgtVal)) {
        return mergeObjectValue(tgtVal, srcVal, replaceArrays);
    }

    return mergeObjectValue({}, srcVal, replaceArrays);
}

function isDeepMergeOptions(value: any): value is TypeDeepMergeOptions {
    return isMergeableObject(value) && 'replaceArrays' in value;
}

// ----------------------------------------------------------------------------

function deepMerge(...args: any[]): TypeDeepMergeTarget {
    let deep = true;
    let replaceArrays = true;
    let index = 0;

    if (typeof args[index] === 'boolean') {
        deep = args[index];
        index++;
    }

    if (isDeepMergeOptions(args[index])) {
        replaceArrays = args[index].replaceArrays;
        index++;
    }

    const target: TypeDeepMergeTarget = isObjectLike(args[index]) ? args[index] : {};

    for (let i = index + 1; i < args.length; i++) {
        mergeInto(target, args[i], deep, replaceArrays);
    }

    return target;
}

// ----------------------------------------------------------------------------

export default deepMerge;
